/**
 * WebSocket Service for real-time chat with streaming responses
 */

export type MessageHandler = (data: string | object) => void
export type ConnectionHandler = () => void

interface WebSocketOptions {
  reconnect?: boolean
  reconnectInterval?: number
  maxReconnectAttempts?: number
}

class WebSocketService {
  private socket: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts: number
  private reconnectInterval: number
  private shouldReconnect: boolean
  private messageHandlers: Set<MessageHandler> = new Set()
  private connectionHandlers: Set<ConnectionHandler> = new Set()
  private disconnectionHandlers: Set<ConnectionHandler> = new Set()
  private isManualClose = false

  constructor(options: WebSocketOptions = {}) {
    this.shouldReconnect = options.reconnect ?? true
    this.reconnectInterval = options.reconnectInterval ?? 3000
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5
  }

  connect(url?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isManualClose = false
      
      const wsUrl = url || this.getWebSocketUrl()
      
      try {
        this.socket = new WebSocket(wsUrl)

        this.socket.onopen = () => {
          console.log('WebSocket connected to', wsUrl)
          this.reconnectAttempts = 0
          this.connectionHandlers.forEach(handler => handler())
          resolve()
        }

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            this.messageHandlers.forEach(handler => handler(data))
          } catch {
            // If not JSON, pass as string
            this.messageHandlers.forEach(handler => handler(event.data))
          }
        }

        this.socket.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason)
          this.disconnectionHandlers.forEach(handler => handler())
          
          if (!this.isManualClose && this.shouldReconnect) {
            this.attemptReconnect()
          }
        }

        this.socket.onerror = (error) => {
          console.warn('WebSocket error:', error)
          // Don't reject if already connected - errors can happen during reconnection
          if (this.socket?.readyState === WebSocket.CONNECTING) {
            reject(new Error('WebSocket connection failed'))
          }
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const token = localStorage.getItem('token')
    const params = token ? `?token=${encodeURIComponent(token)}` : ''
    return `${protocol}//${host}/api/ws/chat${params}`
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)

    setTimeout(() => {
      this.connect().catch(err => {
        console.error('Reconnection failed:', err)
      })
    }, this.reconnectInterval)
  }

  send(data: string | object): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected')
    }

    const message = typeof data === 'string' ? data : JSON.stringify(data)
    this.socket.send(message)
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler)
    return () => this.messageHandlers.delete(handler)
  }

  onConnect(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler)
    return () => this.connectionHandlers.delete(handler)
  }

  onDisconnect(handler: ConnectionHandler): () => void {
    this.disconnectionHandlers.add(handler)
    return () => this.disconnectionHandlers.delete(handler)
  }

  disconnect(): void {
    this.isManualClose = true
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  get readyState(): number | null {
    return this.socket?.readyState ?? null
  }
}

// Singleton instance
export const wsService = new WebSocketService()

// Factory function for creating new instances
export const createWebSocketService = (options?: WebSocketOptions) => 
  new WebSocketService(options)

export default wsService
